import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RoomJoinModal from './RoomJoinModal';
import './RoomsDashboard.css';

function RoomsDashboard({ user }) {
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if we're returning from a room with a rejoin request
    const params = new URLSearchParams(location.search);
    const rejoinRoom = params.get('rejoin');
    
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch rooms
        const roomsResponse = await fetch('/api/rooms', {
          credentials: 'include'
        });
        
        if (!roomsResponse.ok) {
          throw new Error('Failed to fetch rooms');
        }
        
        const roomsData = await roomsResponse.json();
        setRooms(roomsData);
        
        // Try to fetch user settings to get phone number
        try {
          const settingsResponse = await fetch('/api/user/settings', {
            credentials: 'include'
          });
          
          if (settingsResponse.ok) {
            const settingsData = await settingsResponse.json();
            console.log('User settings fetched:', settingsData);
            setUserPhone(settingsData.phone || '');
            console.log('Phone number set to:', settingsData.phone || 'empty');
            
            // If we have a rejoin request and user has phone, show the modal
            if (rejoinRoom && settingsData.phone && settingsData.phone.trim()) {
              setSelectedRoom(rejoinRoom);
              setShowJoinModal(true);
            }
          } else {
            console.log('Settings response not OK:', settingsResponse.status);
          }
        } catch (settingsErr) {
          console.log('Could not fetch user settings:', settingsErr);
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [location]);

  const handleRoomClick = (roomName) => {
    console.log('Room clicked:', roomName);
    console.log('User phone:', userPhone);
    console.log('Phone trimmed:', userPhone?.trim());
    
    // If user has a phone number, show the modal
    if (userPhone && userPhone.trim()) {
      console.log('Showing modal for room:', roomName);
      setSelectedRoom(roomName);
      setShowJoinModal(true);
    } else {
      // Otherwise, join directly via web
      console.log('No phone number, joining directly');
      joinRoomWeb(roomName);
    }
  };
  
  const joinRoomWeb = (roomName) => {
    navigate(`/room/${roomName}`);
  };
  
  const handleModalClose = () => {
    setShowJoinModal(false);
    setSelectedRoom('');
    // Clear the rejoin parameter from URL if present
    const params = new URLSearchParams(location.search);
    if (params.has('rejoin')) {
      params.delete('rejoin');
      const newUrl = params.toString() ? `${location.pathname}?${params.toString()}` : location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  };

  const openBellaKiosk = () => {
    navigate('/bella-kiosk');
  };

  return (
    <>
      <RoomJoinModal 
        isOpen={showJoinModal}
        onClose={handleModalClose}
        onJoinWeb={() => {
          // Clear the active callback flag when joining via web
          const activeCallbackKey = `activeCallback_${selectedRoom}`;
          localStorage.removeItem(activeCallbackKey);
          joinRoomWeb(selectedRoom);
          handleModalClose();
        }}
        onJoinPhone={() => {
          // Phone callback is handled within the modal
        }}
        roomName={selectedRoom}
        phoneNumber={userPhone}
      />
      <div className="dashboard-container">
        <h2>MyJava Active Rooms</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      {isLoading ? (
        <div className="loading">Loading rooms...</div>
      ) : (
        <div className="rooms-grid">
          {rooms.length > 0 ? (
            rooms.map(room => (
              <div 
                key={room.id} 
                className={`room-card ${room.attendees >= room.capacity ? 'room-full' : 'room-available'}`}
                onClick={() => room.attendees < room.capacity && handleRoomClick(room.name)}
              >
                <h3 className="room-name">{room.name}</h3>
                <div className="room-details">
                  <span className="room-capacity">
                    Attendees {room.attendees}/{room.capacity}
                  </span>
                  <span className="room-created">
                    Created: {new Date(room.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                {/* Show speaking indicator for all rooms initially */}
                <div className="speaking-now">
                  <span className="speaking-text">Speaking Now</span>
                  <span className="speaking-indicator">
                    <span className="wave wave1"></span>
                    <span className="wave wave2"></span>
                    <span className="wave wave3"></span>
                  </span>
                </div>
                
                {room.attendees >= room.capacity ? (
                  <div className="room-status full">Room Full</div>
                ) : (
                  <div className="room-status available">Available</div>
                )}
              </div>
            ))
          ) : (
            <div className="no-rooms">No active rooms found</div>
          )}
        </div>
      )}
      </div>
      
      {user && user.role === 'admin' && (
        <div className="dashboard-container kiosk-demo-container">
          <h2>Kiosk Demo</h2>
          <div className="rooms-grid">
            <div 
              className="room-card room-available kiosk-card"
              onClick={openBellaKiosk}
            >
              <h3 className="room-name">Bella</h3>
              <div className="room-details">
                <span className="room-capacity">
                  AVAILABLE
                </span>
              </div>
              <div className="room-status available">Click to Demo</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RoomsDashboard;